import { useCallback } from "react";
import { Task } from "@/types/v2";
import { useEmployeeId } from "@/hooks/useEmployeeId";
import { useRoles } from "@/hooks/useRoles";
import { useStore } from "@/stores/useStore";
import { canModifyTask } from "@/lib/taskSelectors";
import { getEmployeeName } from "@/lib/employees";

/**
 * Single source of truth for who may complete/edit/delete a task:
 * unassigned tasks — anyone; otherwise the assignee or an admin
 * (superAdmins get the same override, matching the app-wide gate pattern).
 */
export const useTaskPermissions = () => {
  const { employeeId } = useEmployeeId();
  const { isAdmin, isSuperAdmin } = useRoles();
  const employees = useStore((s) => s.employees);

  const canModify = useCallback(
    (task: Task) => canModifyTask(task, employeeId, isAdmin || isSuperAdmin),
    [employeeId, isAdmin, isSuperAdmin]
  );

  const modifyDeniedReason = useCallback(
    (task: Task) => {
      const rawName = getEmployeeName(task.assigneeId, employees ?? []);
      const name = rawName && rawName !== "Unknown" ? rawName : "the assignee";
      return `Only ${name} or an admin can modify this task`;
    },
    [employees]
  );

  return { canModify, modifyDeniedReason };
};
